import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { contactMailto } from "@/lib/contact";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import FlipScoreboard from "@/components/FlipScoreboard";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadSimple, ArrowRight, Trash, Sparkle, ImageSquare, DownloadSimple, MagicWand, CheckCircle, XCircle, Vault, Lightning, SealCheck, Export, CaretDown, Warning, Copy, Scales, SignOut, Compass, ChartLineUp, Camera, Crown, Coins, EnvelopeSimple, PlayCircle, FileCsv, CurrencyDollar, ChatsCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MarketplaceGuideModal } from "@/components/MarketplaceGuide";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import BigPictureModal from "@/components/BigPictureModal";

const REQUIRED_1STDIBS = ["Item Title", "Description", "Price", "Images"];

const APPLY_MARKETPLACES = { chairish: "Chairish", "1stdibs": "1stDibs", aptdeco: "AptDeco" };

const MODES = {
  list: {
    label: "I already sell online — cross-list everywhere",
    stage: 2,
    sub: "Pull one owned record from a marketplace you already sell on, then transplant it into others — no re-listing, copying, or pasting.",
  },
  hunt: {
    label: "I hunt for treasures — list my finds",
    stage: 1,
    sub: "From Goodwill racks to flea-market tables — capture your incredible finds and list them for top dollar across every marketplace.",
  },
  explore: {
    label: "Just show me around first",
    stage: 1,
    sub: "Take a look at how Vintage Vantage works — no pressure. Import or capture your first piece whenever you're ready.",
  },
};

const FIELD_FOCUS_MAP = {
  "Item Title": "input-headline",
  "Medium": "input-material",
  "Height": "input-height",
  "Width": "input-width",
  "Depth": "input-depth",
  "Price": "input-price",
  "Images": "media-dropzone",
  "Description": "input-description",
};

const fmtPrice = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");
const greetingTime = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const THROTTLE_MS = 700;

export default function Dashboard() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [enriching, setEnriching] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0, failedIds: [], current: "" });
  const [usage, setUsage] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode, setMode] = useState(() => localStorage.getItem("vv_mode") || "");
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem("vv_mode"));
  const [showGuide, setShowGuide] = useState(false);
  const [bigPictureOpen, setBigPictureOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const { user, logout } = useAuth();
  const [welcome, setWelcome] = useState(null);
  const cancelRef = useRef(false);

  const chooseMode = (m) => {
    setMode(m);
    localStorage.setItem("vv_mode", m);
  };
  const fileRef = useRef();
  const draftsRef = useRef();
  const salesRef = useRef();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: us }] = await Promise.all([
        api.get("/listings"),
        api.get("/usage"),
      ]);
      setListings(data);
      setUsage(us);
    } catch (e) {
      toast.error("Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/dashboard/welcome").then(({ data }) => setWelcome(data)).catch(() => {});
  }, []);

  const handleImport = async (e, source = "listings") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    form.append("source", source);
    try {
      const { data } = await api.post("/import/csv", form);
      const label = source === "drafts" ? "drafts" : source === "sold" ? "sales-history items" : "listings";
      toast.success(`Imported ${data.imported} ${label}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (e.target) e.target.value = "";
    }
  };

  const clearAll = () => {
    setResetConfirmText("");
    setResetOpen(true);
  };

  const confirmReset = async () => {
    setResetting(true);
    try {
      await api.delete("/listings");
      toast.success("Inventory cleared. Ready for a fresh import.");
      setResetOpen(false);
      setResetConfirmText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear inventory");
    } finally {
      setResetting(false);
    }
  };

  const enrichOne = async (id) => {
    setEnriching(id);
    try {
      const { data } = await api.post(`/listings/${id}/enrich`);
      toast.success(`Details added · ${data.images_imported} photos imported`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't fetch details");
    } finally {
      setEnriching(null);
    }
  };

  const runEnrich = async (targets) => {
    if (!targets.length) {
      toast.info("Everything is already filled in");
      return;
    }
    cancelRef.current = false;
    setEnriching("all");
    setProgress({ done: 0, total: targets.length, failed: 0, failedIds: [], current: "" });
    let failed = 0;
    const failedIds = [];
    for (let i = 0; i < targets.length; i++) {
      if (cancelRef.current) break;
      setProgress((p) => ({ ...p, current: targets[i].listing_headline || "Untitled" }));
      try {
        // 120s client-side ceiling — backend caps at 90s, this is the safety net for network stalls
        await api.post(`/listings/${targets[i].id}/enrich`, null, { timeout: 120000 });
      } catch (e) {
        failed++;
        failedIds.push(targets[i].id);
      }
      setProgress((p) => ({ ...p, done: i + 1, failed, failedIds }));
      // Only refresh the listings table every 3 iterations (or on last one) — keeps UI live but
      // avoids piling up load() latency between every enrich
      if ((i + 1) % 3 === 0 || i === targets.length - 1) {
        await load();
      }
      if (i < targets.length - 1) await sleep(THROTTLE_MS);
    }
    const stopped = cancelRef.current;
    setEnriching(null);
    const filled = targets.length - failed;
    if (stopped) {
      toast.info(`Stopped · filled ${filled} of ${targets.length}${failed ? ` · ${failed} to retry` : ""}`);
    } else if (failed > 0) {
      toast.warning(`Filled ${filled} of ${targets.length} · ${failed} need retry — tap "Retry failed" below`);
    } else {
      toast.success(`Done · filled ${filled} of ${targets.length} listings`);
    }
    await load();
  };

  const retryFailed = () => {
    const ids = progress.failedIds || [];
    if (!ids.length) return;
    const targets = listings.filter((l) => ids.includes(l.id) && l.external_product_id);
    if (!targets.length) {
      toast.info("Nothing to retry");
      return;
    }
    runEnrich(targets);
  };

  const enrichAll = () => runEnrich(listings.filter((l) => l.external_product_id && !l.enriched));

  const bulkAutoFill = () => {
    const targets = listings.filter((l) => selectedIds.includes(l.id) && l.external_product_id);
    runEnrich(targets);
  };

  const bulkApprove = async () => {
    const targets = listings.filter((l) => selectedIds.includes(l.id) && l.status !== "Exported");
    if (!targets.length) { toast.info("Nothing to approve in your selection"); return; }
    let ok = 0, blocked = false;
    for (const t of targets) {
      try {
        await api.post(`/listings/${t.id}/approve`);
        ok++;
      } catch (e) {
        if (e.response?.status === 402) { blocked = true; break; }
      }
    }
    await load();
    if (blocked) toast.warning(`Approved ${ok} — free trial used up. Upgrade in the Review Workspace to continue.`);
    else toast.success(`Approved ${ok} listing${ok !== 1 ? "s" : ""} for cross-listing`);
  };

  const cancelEnrichAll = () => {
    cancelRef.current = true;
    toast.message("Finishing the current item, then stopping…");
  };

  const toggleSelect = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = listings.length > 0 && selectedIds.length === listings.length;
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? [] : listings.map((l) => l.id));

  const exportedCount = listings.filter((l) => l.status === "Exported").length;

  const [preview, setPreview] = useState({ open: false, loading: false, data: null, dialect: null, scope: null, ids: null });

  const openPreview = async (dialect, scope, ids = null) => {
    const count = ids ? ids.length : scope === "approved" ? exportedCount : listings.length;
    if (count === 0) {
      toast.info(scope === "approved" ? "Approve some listings first to export them" : "No listings to export yet");
      return;
    }
    setPreview({ open: true, loading: true, data: null, dialect, scope, ids });
    const qs = ids ? `ids=${ids.join(",")}` : `scope=${scope}`;
    try {
      const { data } = await api.get(`/export/${dialect}/preview?${qs}`);
      setPreview((p) => ({ ...p, loading: false, data }));
    } catch (e) {
      toast.error("Could not load export preview");
      setPreview({ open: false, loading: false, data: null, dialect: null, scope: null, ids: null });
    }
  };

  const downloadExport = (dialect, scope, ids = null) => {
    const count = ids ? ids.length : scope === "approved" ? exportedCount : listings.length;
    if (count === 0) {
      toast.info("Nothing to export yet");
      return;
    }
    const qs = ids ? `ids=${ids.join(",")}` : `scope=${scope}`;
    window.open(`${API}/export/${dialect}.csv?${qs}`, "_blank");
    toast.success(`Downloading ${count} listing${count > 1 ? "s" : ""} as a 1stDibs CSV…`);
  };

  const exportSelected = () => openPreview("1stdibs", null, selectedIds);

  const [appPack, setAppPack] = useState({ open: false, loading: false, items: [] });
  const openAppPack = async () => {
    if (!selectedIds.length) { toast.info("Select the items you want for your application first"); return; }
    setAppPack({ open: true, loading: true, items: [] });
    try {
      const { data } = await api.get(`/export/1stdibs/preview?ids=${selectedIds.join(",")}`);
      const items = (data.rows || []).map((r) => ({
        id: r._listing_id,
        title: r["Item Title"] || "",
        description: r["Description"] || "",
        price: r["Price"] || "",
        images: (r["Images"] || "").split(" | ").map((s) => s.trim()).filter(Boolean),
      }));
      setAppPack({ open: true, loading: false, items });
    } catch (e) {
      toast.error("Could not build application pack");
      setAppPack({ open: false, loading: false, items: [] });
    }
  };

  const counts = {
    total: listings.length,
    ready: listings.filter((l) => l.review_status === "ready").length,
    fullReady: listings.filter((l) => l.full_ready).length,
    drafts: listings.filter((l) => l.status === "Draft").length,
    exported: listings.filter((l) => l.status === "Exported").length,
  };
  const currentStage = mode && MODES[mode] ? MODES[mode].stage : counts.exported > 0 ? 2 : 1;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Compass motif — always present, always guiding. Subtle fixed watermark behind the content. */}
      <img
        src="/loop/compass.png"
        alt=""
        aria-hidden="true"
        data-testid="dashboard-compass-watermark"
        className="pointer-events-none select-none fixed right-[-6rem] bottom-[-7rem] w-[30rem] max-w-[60vw] opacity-[0.04] z-0"
      />
      <AnnouncementBanner />
      {/* Top bar */}
      <header className="border-b border-[#E5E5E5] bg-[#FDFBF7]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-14 w-14 rounded-full object-cover ring-1 ring-[#E5E0D6] shrink-0" data-testid="app-logo" />
            <div>
              <h1 className="font-serif-display text-2xl leading-none font-bold tracking-tight" data-testid="app-title">
                Vintage Vantage
              </h1>
              <p className="label-caps mt-1">Cross-Listing Studio</p>
            </div>
          </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:justify-end">
          <Select value={mode} onValueChange={chooseMode}>
            <SelectTrigger data-testid="mode-select" className="w-[210px] h-10 border-[#E5E5E5] bg-white text-sm">
              <SelectValue placeholder="What do you want to do?" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(MODES).map(([k, m]) => (
                <SelectItem key={k} value={k} data-testid={`mode-option-${k}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {usage && (
            <button
              data-testid="free-trial-pill"
              onClick={() => navigate("/plans")}
              title="View membership plans"
              className={`text-sm font-semibold px-3 py-2 rounded-full border transition-colors ${usage.unlimited ? "bg-[#0D1B2A] text-[#FDFBF7] border-[#0D1B2A]" : usage.locked ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0] hover:bg-[#D1FAE5]"}`}
            >
              {usage.unlimited
                ? `${usage.label} · Unlimited`
                : usage.plan === "free"
                ? (usage.locked ? "Free listings used — Upgrade" : `${usage.remaining}/${usage.free_limit} free listings`)
                : (usage.locked ? `${usage.monthly_cap} used this month` : `${usage.remaining}/${usage.monthly_cap} cross-lists this month`)}
            </button>
          )}
          <button
            data-testid="enrich-all-button"
            disabled={!listings.length || enriching === "all"}
            onClick={enrichAll}
            className="border border-[#0D1B2A] text-[#0D1B2A] px-4 py-2.5 rounded-md hover:bg-[#0D1B2A] hover:text-white transition-colors font-medium inline-flex items-center gap-2 disabled:opacity-40"
          >
            <MagicWand size={16} weight="fill" /> {enriching === "all" ? "Filling in…" : "Auto-Fill All from Chairish"}
          </button>
          <div className="relative">
            <button
              data-testid="export-menu-button"
              disabled={!listings.length}
              onClick={() => setExportOpen((o) => !o)}
              className="border border-[#0D1B2A] text-[#0D1B2A] px-4 py-2.5 rounded-md hover:bg-[#0D1B2A] hover:text-white transition-colors font-medium inline-flex items-center gap-2 disabled:opacity-40"
            >
              <Export size={16} weight="bold" /> Export <CaretDown size={13} />
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#E5E5E5] rounded-lg shadow-xl z-50 py-1.5 text-left" data-testid="export-menu">
                  <div className="px-3 py-1.5 label-caps flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> 1stDibs · Dialect B
                  </div>
                  <button data-testid="export-1stdibs-approved" onClick={() => { setExportOpen(false); openPreview("1stdibs", "approved"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <DownloadSimple size={15} /> Approved listings ({exportedCount})
                  </button>
                  <button data-testid="export-1stdibs-all" onClick={() => { setExportOpen(false); openPreview("1stdibs", "all"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <DownloadSimple size={15} /> All listings ({listings.length})
                  </button>
                  <div className="border-t border-[#F0F0F0] my-1" />
                  <div className="px-3 py-2 text-sm text-[#A3A3A3]">AptDeco · Dialect C — coming soon</div>
                </div>
              </>
            )}
          </div>
          <button
            data-testid="open-workspace-button"
            disabled={!listings.length}
            onClick={() => navigate("/review")}
            className="bg-[#0D1B2A] text-white px-5 py-2.5 rounded-md hover:bg-black transition-colors font-medium inline-flex items-center gap-2 disabled:opacity-40"
          >
            Open Review Workspace <ArrowRight size={16} weight="bold" />
          </button>
          <div className="relative">
            <button
              data-testid="user-menu-button"
              onClick={() => setUserMenuOpen((o) => !o)}
              title={user?.email}
              className="h-11 w-11 rounded-full bg-[#0D1B2A] text-white flex items-center justify-center font-bold uppercase text-lg hover:bg-black transition-colors"
            >
              {(user?.name || user?.email || "?").slice(0, 1)}
            </button>
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-60 bg-white border border-[#E5E5E5] rounded-lg shadow-xl z-50 py-1.5 text-left" data-testid="user-menu">
                  <div className="px-3 py-2 border-b border-[#F0F0F0]">
                    <p className="text-sm font-semibold truncate">{user?.name || "Seller"}</p>
                    <p className="text-xs text-[#737373] truncate">{user?.email}</p>
                  </div>
                  <button data-testid="nav-watch-story-button" onClick={() => { setUserMenuOpen(false); navigate("/?tour=1"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2 font-semibold text-[#B47A3C]">
                    <PlayCircle size={16} weight="fill" /> Watch the story
                  </button>
                  <button data-testid="nav-portfolio-button" onClick={() => { setUserMenuOpen(false); navigate("/portfolio"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <Vault size={16} weight="fill" /> My Portfolio
                  </button>
                  <button data-testid="nav-finds-button" onClick={() => { setUserMenuOpen(false); navigate("/finds"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <Camera size={16} weight="fill" /> Fantastic Finds
                  </button>
                  <button data-testid="nav-pricing-button" onClick={() => { setUserMenuOpen(false); navigate("/pricing"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <ChartLineUp size={16} weight="fill" /> Pricing tool
                  </button>
                  {(user?.is_admin || user?.plan_status?.plan === "founding") && (
                    <button data-testid="nav-profitability-button" onClick={() => { setUserMenuOpen(false); navigate("/profitability"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                      <Coins size={16} weight="fill" /> Net by marketplace
                      <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0D1B2A] text-[#FBBF24]">Beta</span>
                    </button>
                  )}
                  <button data-testid="nav-plans-button" onClick={() => { setUserMenuOpen(false); navigate("/plans"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <Crown size={16} weight="fill" /> Membership & billing
                  </button>
                  <button data-testid="nav-guide-button" onClick={() => { setUserMenuOpen(false); setShowGuide(true); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <Scales size={16} weight="fill" /> Compare platforms
                  </button>
                  <button data-testid="nav-community-button" onClick={() => { setUserMenuOpen(false); navigate("/community"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <ChatsCircle size={16} weight="fill" /> Seller Community
                  </button>
                  <button data-testid="nav-roadmap-button" onClick={() => { setUserMenuOpen(false); navigate("/roadmap"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <Compass size={16} weight="fill" /> Feature roadmap
                  </button>
                  <button data-testid="nav-big-picture-button" onClick={() => { setUserMenuOpen(false); navigate("/big-picture"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <Compass size={16} weight="fill" /> The Big Picture
                  </button>
                  <a data-testid="nav-contact-link" href={contactMailto("Vintage Vantage — a note from my dashboard")} onClick={() => setUserMenuOpen(false)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2">
                    <EnvelopeSimple size={16} weight="fill" /> Contact us
                  </a>
                  <div className="border-t border-[#F0F0F0] my-1" />
                  <button data-testid="logout-button" onClick={handleLogout} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FDFBF7] inline-flex items-center gap-2 text-[#B91C1C]">
                    <SignOut size={16} weight="bold" /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </header>

      {(enriching === "all" || (progress.failedIds && progress.failedIds.length > 0)) && (
        <div className="border-b border-[#E5E5E5] bg-white sticky top-0 z-40 shadow-sm" data-testid="enrich-progress-panel">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <MagicWand size={16} weight="fill" className={`text-[#0D1B2A] ${enriching === "all" ? "animate-pulse" : ""}`} />
                <span className="font-medium text-sm">
                  {enriching === "all"
                    ? `Filling in details from Chairish… ${progress.done} of ${progress.total}`
                    : `Finished — ${progress.done - progress.failed} of ${progress.total} filled · ${progress.failed} need retry`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {enriching !== "all" && progress.failedIds && progress.failedIds.length > 0 && (
                  <button onClick={retryFailed} data-testid="retry-failed-button" className="text-xs font-semibold text-white bg-[#0D1B2A] hover:bg-black px-3 py-1.5 rounded-md inline-flex items-center gap-1">
                    <MagicWand size={12} weight="fill" /> Retry {progress.failedIds.length} failed
                  </button>
                )}
                {enriching !== "all" && progress.failedIds && progress.failedIds.length > 0 && (
                  <button
                    onClick={() => setProgress({ done: 0, total: 0, failed: 0, failedIds: [], current: "" })}
                    data-testid="dismiss-progress-button"
                    className="text-xs font-semibold text-[#737373] hover:text-[#0D1B2A] inline-flex items-center gap-1"
                  >
                    Dismiss
                  </button>
                )}
                {enriching === "all" && (
                  <button onClick={cancelEnrichAll} data-testid="cancel-enrich-button" className="text-xs font-semibold text-[#B91C1C] hover:underline inline-flex items-center gap-1">
                    <XCircle size={14} /> Stop
                  </button>
                )}
              </div>
            </div>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} className="h-2" data-testid="enrich-progress-bar" />
            <p className="text-xs text-[#737373] mt-2 truncate">
              {enriching === "all"
                ? (progress.current ? `Currently: ${progress.current}` : "Starting…")
                : "Chairish scrape completed — you can retry the ones that timed out, or leave them and revisit anytime."}
              {enriching === "all" && progress.failed > 0 && <span className="text-[#B91C1C]"> · {progress.failed} skipped</span>}
            </p>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-10 relative z-10">
        {welcome && (welcome.total_listings > 0 || welcome.researched_week > 0) && (
          <section data-testid="welcome-panel" className="mb-9 vv-fade-up">
            <div className="relative overflow-hidden vv-grain bg-[#0D1B2A] text-[#FDFBF7] rounded-2xl p-6 sm:p-8">
              <p className="label-caps mb-1.5" style={{ color: "#B47A3C" }}>Welcome back</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight mb-6" data-testid="welcome-greeting">
                {greetingTime()}, {welcome.name}.
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {[
                  {
                    icon: Camera,
                    value: welcome.researched_yesterday || welcome.researched_week || 0,
                    label: welcome.researched_yesterday ? "researched yesterday" : "researched this week",
                  },
                  {
                    icon: SealCheck,
                    value: welcome.purchased_week || welcome.purchased_total || 0,
                    label: "Fantastic Finds bought",
                  },
                  { icon: Vault, value: welcome.drafts_waiting || 0, label: "drafts waiting" },
                  { icon: ChartLineUp, value: fmtPrice(welcome.inventory_value), label: "inventory value" },
                ].map((s, i) => (
                  <div key={i} data-testid={`welcome-stat-${i}`} className="border border-white/10 bg-white/[0.04] rounded-xl p-4">
                    <s.icon size={18} weight="fill" className="text-[#B47A3C] mb-2" />
                    <p className="font-serif-display text-3xl font-bold leading-none">{s.value}</p>
                    <p className="text-[13px] text-[#B8B8B8] mt-1.5 leading-snug">{s.label}</p>
                  </div>
                ))}
              </div>
              {welcome.newest_listing && (
                <div className="mt-5 flex items-center gap-2 text-[15px] text-[#D8D3C8]">
                  <Sparkle size={16} weight="fill" className="text-[#B47A3C] shrink-0" />
                  <span>Newest listing:&nbsp;</span>
                  <span className="text-[#FDFBF7] font-semibold truncate">{welcome.newest_listing}</span>
                </div>
              )}
            </div>
          </section>
        )}
        <FlipScoreboard />
        <section className="mb-9" data-testid="value-prop-hero">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/finds")}
              data-testid="hero-lens-launcher"
              title="Scan a fantastic find"
              className="shrink-0 group"
              aria-label="Scan a fantastic find"
            >
              <img
                src="/compass_lens.png"
                alt="Scan a fantastic find with your camera"
                className="h-16 w-16 sm:h-20 sm:w-20 object-contain transition-transform duration-300 group-hover:scale-105 group-hover:rotate-6"
              />
            </button>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              List your fantastic finds — in record time.
            </h2>
          </div>
          <p className="text-[#525252] mt-2 max-w-2xl leading-relaxed">
            {mode && MODES[mode] ? MODES[mode].sub : "Vintage Vantage restores ownership of every detail you've already created — then lets you cross-list it anywhere, with a review step so nothing slips through."}
          </p>

          <div className="flex flex-wrap gap-3 mt-5" data-testid="hero-primary-actions">
            <button
              data-testid="hero-scan-finds-button"
              onClick={() => navigate("/finds")}
              className="bg-[#0D1B2A] text-white px-5 py-3 rounded-md hover:bg-black transition-colors font-semibold inline-flex items-center gap-2"
            >
              <Camera size={18} weight="fill" /> Scan or upload a find
            </button>
            <button
              data-testid="hero-import-inventory-button"
              onClick={() => fileRef.current?.click()}
              className="border border-[#0D1B2A] text-[#0D1B2A] px-5 py-3 rounded-md hover:bg-[#FDFBF7] transition-colors font-semibold inline-flex items-center gap-2"
            >
              <UploadSimple size={18} /> Import CSV inventory
            </button>
          </div>
          <p className="text-xs text-[#8A867D] mt-2" data-testid="hero-primary-actions-hint">
            <Camera size={12} weight="fill" className="inline mb-0.5" /> Take a photo <em>or</em> upload from your phone library · <UploadSimple size={12} className="inline mb-0.5" /> CSV = Chairish / Google Sheet export
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mt-6" data-testid="value-pillars">
            {[
              { icon: Vault, title: "Restore ownership", body: "Reclaim countless hours of reproduction effort — your descriptions, dimensions and photos, saved once and owned by you." },
              { icon: Lightning, title: "Cross-platform without re-typing", body: "Translate that single record into each marketplace's dialect — export-ready to list everywhere, no re-typing." },
              { icon: SealCheck, title: "Approval workflow", body: "A guided, user-approved review so a dimension, photo or detail is never missed before you list it." },
            ].map((p) => (
              <div key={p.title} data-testid={`pillar-${p.title.split(" ")[0].toLowerCase()}`} className="border border-[#E5E5E5] rounded-xl bg-white p-4">
                <div className="h-9 w-9 rounded-md bg-[#0D1B2A] flex items-center justify-center mb-3">
                  <p.icon size={18} color="#FDFBF7" weight="fill" />
                </div>
                <p className="font-semibold text-base mb-1">{p.title}</p>
                <p className="text-sm text-[#525252] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="dialect-chips">
            <span className="label-caps mr-1">Speaks</span>
            {[
              { m: "Chairish", d: "Dialect A", live: true },
              { m: "1stDibs", d: "Dialect B", live: true },
              { m: "AptDeco", d: "Dialect C", live: false },
            ].map((x) => (
              <span
                key={x.m}
                data-testid={`dialect-${x.m.toLowerCase()}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${x.live ? "border-[#0D1B2A] bg-white text-[#0D1B2A]" : "border-[#E5E5E5] bg-[#FDFBF7] text-[#A3A3A3]"}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${x.live ? "bg-[#10B981]" : "bg-[#D4D4D4]"}`} />
                {x.m} <span className="opacity-60">· {x.d}</span>{!x.live && <span className="opacity-60">· soon</span>}
              </span>
            ))}
            <button
              onClick={() => setShowGuide(true)}
              data-testid="compare-platforms-button"
              className="ml-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D1B2A] underline decoration-[#D4D4D4] underline-offset-4 hover:decoration-[#0D1B2A] transition-colors"
            >
              <Scales size={15} weight="fill" /> Compare these platforms
            </button>
          </div>

          <div className="mt-7 border-t border-[#E5E5E5] pt-6" data-testid="process-stepper">
            <p className="label-caps mb-3">Your seller journey</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { title: "Capture or import your pieces", body: "Scan a find with the camera or import your inventory — we recover every detail you've already created." },
                { title: "Build full listings", body: `Add dimensions, materials & period — all kept and ready to transfer. ${counts.fullReady}/${counts.total || 0} have full details.` },
                { title: "Cross-list everywhere", body: "Translate one owned record into every marketplace's dialect — export-ready to list wherever each piece fits." },
              ].map((s, i) => {
                const active = currentStage === i + 1;
                return (
                  <div key={s.title} data-testid={`stage-${i + 1}`} className={`rounded-xl border p-4 ${active ? "border-[#0D1B2A] bg-white ring-1 ring-[#0D1B2A]" : "border-[#E5E5E5] bg-white"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-[#0D1B2A] text-white" : "bg-[#F0F0F0] text-[#737373]"}`}>{i + 1}</span>
                      <span className="font-semibold text-base">{s.title}</span>
                      {active && <span className="ml-auto text-xs font-bold bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0] px-2 py-0.5 rounded-full">You're here</span>}
                    </div>
                    <p className="text-sm text-[#525252] mt-2 leading-relaxed">{s.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className="grid md:grid-cols-3 gap-6 items-start">
          {/* Import card */}
          <div className="md:col-span-1">
            <div className="border border-[#E5E5E5] rounded-xl bg-white p-6">
              <p className="label-caps">Step 1</p>
              <h2 className="font-serif-display text-2xl font-semibold mt-1 mb-1">Import CSV inventory</h2>
              <p className="text-base text-[#525252] mb-5">
                Already selling on Chairish or keeping your own spreadsheet? Export your <strong>Google Sheet to CSV</strong> and drop it here — or
                use a Chairish CSV export. Columns auto-map; all you need is a <strong>Title</strong> to start.
                Bring in your <strong>active listings, drafts, and full sales history</strong> — we capture all of it.
                <br /><span className="text-sm text-[#8A867D] italic">Looking to upload a photo instead? Use "Scan or upload a find" above.</span>
              </p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleImport(e, "listings")} data-testid="csv-file-input" />
              <input ref={draftsRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleImport(e, "drafts")} data-testid="csv-drafts-input" />
              <input ref={salesRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleImport(e, "sold")} data-testid="csv-sales-input" />
              <button
                data-testid="import-csv-button"
                onClick={() => fileRef.current?.click()}
                disabled={importing}
                className="w-full border-2 border-dashed border-[#D4D4D4] rounded-lg py-10 flex flex-col items-center gap-2 hover:border-[#0D1B2A] hover:bg-[#FDFBF7] transition-colors"
              >
                <UploadSimple size={28} weight="light" />
                <span className="font-medium">{importing ? "Importing…" : "Choose CSV file"}</span>
                <span className="text-sm text-[#737373]">Active listings · Google Sheet → File → Download → CSV</span>
              </button>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  data-testid="import-drafts-button"
                  onClick={() => draftsRef.current?.click()}
                  disabled={importing}
                  className="border border-[#D4D4D4] rounded-lg py-3 px-2 flex items-center justify-center gap-2 text-sm font-medium hover:border-[#0D1B2A] hover:bg-[#FDFBF7] transition-colors"
                >
                  <FileCsv size={18} weight="light" /> Upload drafts
                </button>
                <button
                  data-testid="import-sales-button"
                  onClick={() => salesRef.current?.click()}
                  disabled={importing}
                  className="border border-[#D4D4D4] rounded-lg py-3 px-2 flex items-center justify-center gap-2 text-sm font-medium hover:border-[#0D1B2A] hover:bg-[#FDFBF7] transition-colors"
                >
                  <CurrencyDollar size={18} weight="light" /> Upload sales history
                </button>
              </div>
              {listings.length > 0 && (
                <button onClick={clearAll} data-testid="clear-all-button" className="mt-4 text-sm text-[#B91C1C] inline-flex items-center gap-1 hover:underline">
                  <Trash size={15} /> Clear all data
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              {[["Total", counts.total], ["Ready", counts.ready], ["Drafts", counts.drafts], ["Submitted", counts.exported]].map(([k, v]) => (
                <div key={k} className="border border-[#E5E5E5] rounded-lg bg-white p-4">
                  <div className="font-serif-display text-3xl font-bold leading-none">{v}</div>
                  <div className="label-caps mt-2">{k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Listings table */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-display text-2xl font-semibold">Inventory</h2>
              <p className="label-caps">{listings.length} items</p>
            </div>

            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-lg bg-[#0D1B2A] text-white" data-testid="bulk-action-bar">
                <span className="text-sm font-medium px-2">{selectedIds.length} selected</span>
                <button data-testid="bulk-autofill" onClick={bulkAutoFill} disabled={enriching === "all"} className="text-sm font-semibold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
                  <MagicWand size={15} weight="fill" /> Auto-Fill selected
                </button>
                <button data-testid="bulk-approve" onClick={bulkApprove} className="text-sm font-semibold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md inline-flex items-center gap-1.5">
                  <CheckCircle size={15} weight="fill" /> Approve selected
                </button>
                <button data-testid="bulk-export" onClick={exportSelected} className="text-sm font-semibold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md inline-flex items-center gap-1.5">
                  <Export size={15} weight="bold" /> Export CSV
                </button>
                <button data-testid="bulk-clear" onClick={() => setSelectedIds([])} className="text-sm text-white/70 hover:text-white px-2 ml-auto">Clear</button>
              </div>
            )}

            <div className="border border-[#E5E5E5] rounded-xl bg-white overflow-hidden">
              {loading ? (
                <div className="p-10 text-center text-[#737373]">Loading…</div>
              ) : listings.length === 0 ? (
                <div className="p-16 text-center" data-testid="empty-state">
                  <Sparkle size={32} className="mx-auto mb-3 text-[#A3A3A3]" />
                  <p className="font-serif-display text-xl font-medium">No listings yet</p>
                  <p className="text-sm text-[#737373] mt-1">Import a CSV to get started.</p>
                </div>
              ) : (
                <table className="w-full text-base" data-testid="listings-table">
                  <thead>
                    <tr className="border-b border-[#E5E5E5] text-left">
                      <th className="pl-4 pr-1 py-3">
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="select-all-checkbox" className="h-4 w-4 accent-[#0D1B2A] align-middle" />
                      </th>
                      <th className="px-4 py-3 label-caps font-semibold">Item</th>
                      <th className="px-4 py-3 label-caps font-semibold">Photos</th>
                      <th className="px-4 py-3 label-caps font-semibold">Price</th>
                      <th className="px-4 py-3 label-caps font-semibold">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((l) => (
                      <tr key={l.id} className={`border-b border-[#F0F0F0] hover:bg-[#FDFBF7] transition-colors ${selectedIds.includes(l.id) ? "bg-[#FDFBF7]" : ""}`} data-testid={`listing-row-${l.id}`}>
                        <td className="pl-4 pr-1 py-3">
                          <input type="checkbox" checked={selectedIds.includes(l.id)} onChange={() => toggleSelect(l.id)} data-testid={`select-${l.id}`} className="h-4 w-4 accent-[#0D1B2A] align-middle" />
                        </td>
                        <td className="px-4 py-3 max-w-[280px]">
                          <div className="font-medium truncate">{l.listing_headline || "Untitled"}</div>
                          <div className="text-sm text-[#737373]">{l.period_era || l.seller_sku || "—"}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-[#525252]"><ImageSquare size={15} />{l.image_count}</span>
                        </td>
                        <td className="px-4 py-3">{fmtPrice(l.price_retail)}</td>
                        <td className="px-4 py-3"><StatusBadge status={l.review_status} testid={`status-${l.id}`} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-3 justify-end">
                            {l.external_product_id && (
                              <button
                                data-testid={`enrich-button-${l.id}`}
                                disabled={enriching === l.id}
                                onClick={() => enrichOne(l.id)}
                                title={l.enriched ? "Refresh details from Chairish" : "Get dimensions, details & photos from Chairish"}
                                className={`font-medium inline-flex items-center gap-1 transition-colors ${l.enriched ? "text-[#047857]" : "text-[#525252] hover:text-[#0D1B2A]"}`}
                              >
                                <DownloadSimple size={14} weight={l.enriched ? "fill" : "regular"} />
                                {enriching === l.id ? "…" : l.enriched ? "Details Added" : "Auto-Fill"}
                              </button>
                            )}
                            <button
                              data-testid={`review-button-${l.id}`}
                              onClick={() => navigate(`/review/${l.id}`)}
                              className="text-[#0D1B2A] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                            >
                              Review <ArrowRight size={14} weight="bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>

      <ExportPreviewModal
        state={preview}
        onClose={() => setPreview((p) => ({ ...p, open: false }))}
        onDownload={() => downloadExport(preview.dialect, preview.scope, preview.ids)}
      />
      <ApplicationPackModal
        state={appPack}
        onClose={() => setAppPack((p) => ({ ...p, open: false }))}
      />
      <MarketplaceGuideModal open={showGuide} onClose={() => setShowGuide(false)} onExplore={(t) => { if (t === "workspace") navigate("/review"); }} />
      <BigPictureModal open={bigPictureOpen} onOpenChange={setBigPictureOpen} />

      <Dialog open={showOnboarding} onOpenChange={(o) => { if (!o && mode) setShowOnboarding(false); }}>
        <DialogContent className="max-w-lg" data-testid="onboarding-modal">
          <DialogHeader>
            <div className="inline-flex items-center gap-2 mb-1">
              <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#E5E0D6]" />
              <span className="label-caps">List your fantastic finds in record time</span>
            </div>
            <DialogTitle className="font-serif-display text-3xl font-bold tracking-tight">
              List your fantastic finds — in record time.
            </DialogTitle>
            <DialogDescription className="text-[#525252]">
              First — where are you starting from? Pick one and we'll tailor everything to it. You can switch anytime from the top bar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5 mt-1">
            {Object.entries(MODES).map(([k, m], i) => (
              <button
                key={k}
                data-testid={`onboarding-${k}`}
                onClick={() => chooseMode(k)}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${mode === k ? "border-[#0D1B2A] bg-[#FDFBF7] ring-1 ring-[#0D1B2A]" : "border-[#E5E5E5] hover:border-[#0D1B2A]"}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${mode === k ? "bg-[#0D1B2A] text-white" : "bg-[#F0F0F0] text-[#737373]"}`}>{i + 1}</span>
                  <div>
                    <p className="font-semibold text-base">{m.label}</p>
                    <p className="text-sm text-[#525252] mt-0.5 leading-relaxed">{m.sub}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="reset-inventory-modal">
          <DialogHeader>
            <div className="inline-flex items-center gap-2 mb-1">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FEE2E2] text-[#B91C1C]">
                <Warning size={20} weight="fill" />
              </span>
              <span className="label-caps text-[#B91C1C]">Danger zone</span>
            </div>
            <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">
              Delete every listing in this account?
            </DialogTitle>
            <DialogDescription className="text-[#525252]">
              This will permanently remove <strong>all {listings.length} listing{listings.length === 1 ? "" : "s"}</strong> and their associated photos from
              this account. Your original CSV file on your computer is untouched — you can always re-import.
              <br /><br />
              To confirm, type <span className="font-mono font-bold text-[#B91C1C]">DELETE</span> in the box below.
            </DialogDescription>
          </DialogHeader>
          <input
            data-testid="reset-confirm-input"
            value={resetConfirmText}
            onChange={(e) => setResetConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="w-full border border-[#D4D4D4] rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
            autoFocus
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              data-testid="reset-cancel-button"
              onClick={() => setResetOpen(false)}
              disabled={resetting}
              className="border border-[#E5E5E5] bg-white text-[#0D1B2A] px-4 py-2 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50"
            >
              Cancel — keep my listings
            </button>
            <button
              data-testid="reset-confirm-button"
              onClick={confirmReset}
              disabled={resetConfirmText !== "DELETE" || resetting}
              className="bg-[#B91C1C] text-white px-4 py-2 rounded-md hover:bg-[#991B1B] transition-colors font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <Trash size={15} weight="bold" /> {resetting ? "Deleting…" : "Yes, delete everything"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApplicationPackModal({ state, onClose }) {
  const navigate = useNavigate();
  const { open, loading, items } = state;

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text || "");
    toast.success(`${label} copied`);
  };

  const copyAll = (it) => {
    const txt = `Item Title:\n${it.title}\n\nDescription:\n${it.description}\n\nPrice (USD): $${it.price}`;
    navigator.clipboard?.writeText(txt);
    toast.success("Title, description & price copied");
  };

  const downloadImage = async (url, name) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      window.open(url, "_blank");
    }
  };

  const downloadAll = async (item) => {
    const base = (item.title || "item").slice(0, 30).replace(/[^a-z0-9]/gi, "_");
    toast.message(`Downloading ${item.images.length} photos…`);
    for (let i = 0; i < item.images.length; i++) {
      await downloadImage(item.images[i], `${base}_${i + 1}.jpg`);
      await new Promise((r) => setTimeout(r, 300));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="application-pack-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[#0D1B2A] mb-1">
            <SealCheck size={18} weight="fill" />
            <span className="label-caps">1stDibs Seller Application · sample pack</span>
          </div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">
            Your application items, ready to paste
          </DialogTitle>
          <DialogDescription className="text-[#525252]">
            The 1stDibs application form takes one item at a time — Item Title, Description, $ USD, and an
            image. For each piece below, copy the text straight into the form and download the photos to drag in.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center text-[#737373]">Building your pack…</div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="application-pack-list">
            {items.map((it, idx) => (
              <div key={it.id} className="border border-[#E5E5E5] rounded-xl p-4" data-testid={`pack-item-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="label-caps">Item {idx + 1} of {items.length}</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => copyAll(it)} data-testid={`pack-copyall-${idx}`} className="text-sm font-semibold text-[#0D1B2A] inline-flex items-center gap-1 hover:underline">
                      <Copy size={14} /> Copy all
                    </button>
                    <button onClick={() => { onClose(); navigate(`/review/${it.id}`); }} className="text-sm text-[#525252] font-medium inline-flex items-center gap-1 hover:text-[#0D1B2A] transition-colors" data-testid={`pack-edit-${idx}`}>
                      Edit <ArrowRight size={13} weight="bold" />
                    </button>
                  </div>
                </div>

                <PackField label="Item Title" value={it.title} onCopy={() => copy(it.title, "Title")} onFix={!it.title ? () => { onClose(); navigate(`/review/${it.id}?focus=input-headline`); } : null} testid={`pack-title-${idx}`} />
                <PackField label="Description of Item" value={it.description} multiline onCopy={() => copy(it.description, "Description")} onFix={!it.description ? () => { onClose(); navigate(`/review/${it.id}?focus=input-description`); } : null} testid={`pack-desc-${idx}`} />
                <PackField label="Price ($ USD)" value={it.price ? `$${it.price}` : ""} onCopy={() => copy(it.price, "Price")} onFix={!it.price ? () => { onClose(); navigate(`/review/${it.id}?focus=input-price`); } : null} testid={`pack-price-${idx}`} />

                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="label-caps">Images ({it.images.length})</p>
                    {it.images.length > 0 && (
                      <button
                        onClick={() => downloadAll(it)}
                        data-testid={`pack-dlall-${idx}`}
                        className="text-sm font-semibold text-[#0D1B2A] inline-flex items-center gap-1 hover:underline"
                      >
                        <DownloadSimple size={14} /> Download all photos
                      </button>
                    )}
                  </div>
                  {it.images.length === 0 ? (
                    <button onClick={() => { onClose(); navigate(`/review/${it.id}?focus=media-dropzone`); }} className="inline-flex items-center gap-1 rounded bg-[#FEF3C7] text-[#B45309] font-medium px-2.5 py-1 text-xs hover:bg-[#FDE68A]" data-testid={`pack-addphotos-${idx}`}>
                      <Warning size={12} weight="fill" /> Add photos
                    </button>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {it.images.map((url, i) => (
                        <div key={i} className="relative group">
                          <img
                            src={url}
                            alt=""
                            onClick={() => window.open(url, "_blank")}
                            title="Click to view full size"
                            className="h-16 w-16 object-cover rounded border border-[#E5E5E5] cursor-zoom-in"
                          />
                          <button onClick={() => downloadImage(url, `${(it.title || "item").slice(0, 30).replace(/[^a-z0-9]/gi, "_")}_${i + 1}.jpg`)} title="Download" data-testid={`pack-img-dl-${idx}-${i}`} className="absolute top-0.5 right-0.5 h-5 w-5 flex items-center justify-center bg-white/90 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                            <DownloadSimple size={12} className="text-[#0D1B2A]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-[#A3A3A3]">Tip: open the 1stDibs application in another tab and paste each field as you go.</p>
          <button onClick={onClose} data-testid="pack-done-button" className="bg-[#0D1B2A] text-white px-5 py-2 rounded-md hover:bg-black transition-colors font-medium text-sm">Done</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PackField({ label, value, multiline, onCopy, onFix, testid }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <p className="label-caps">{label}</p>
        {value ? (
          <button onClick={onCopy} className="text-sm font-semibold text-[#0D1B2A] inline-flex items-center gap-1 hover:underline" data-testid={`${testid}-copy`}>
            <Copy size={14} /> Copy
          </button>
        ) : (
          onFix && (
            <button onClick={onFix} className="text-sm font-semibold text-[#92400E] inline-flex items-center gap-1 hover:underline" data-testid={`${testid}-fix`}>
              <Warning size={13} weight="fill" /> Add
            </button>
          )
        )}
      </div>
      <div data-testid={testid} className={`text-base rounded-md border px-3 py-2 ${value ? "border-[#E5E5E5] bg-[#FDFBF7] text-[#0D1B2A]" : "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]"} ${multiline ? "whitespace-pre-wrap max-h-32 overflow-y-auto" : "truncate"}`}>
        {value || "— missing — click Add to complete this in the editor"}
      </div>
    </div>
  );
}

function ExportPreviewModal({ state, onClose, onDownload }) {
  const navigate = useNavigate();
  const { open, loading, data, scope } = state;
  const rows = data?.rows || [];
  const columns = data?.columns || [];
  const rowMissing = (r) => REQUIRED_1STDIBS.filter((c) => !String(r[c] ?? "").trim());
  const incompleteCount = rows.filter((r) => rowMissing(r).length > 0).length;

  const fixField = (listingId, column) => {
    const focus = FIELD_FOCUS_MAP[column] || "";
    onClose();
    navigate(`/review/${listingId}${focus ? `?focus=${focus}` : ""}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] xl:max-w-6xl" data-testid="export-preview-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[#0D1B2A] mb-1">
            <Export size={18} weight="bold" />
            <span className="label-caps">{data?.name || "1stDibs"} · Dialect B preview</span>
          </div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">
            Review before you submit
          </DialogTitle>
          <DialogDescription className="text-[#525252]">
            The 1stDibs application only needs four basics — <strong>title, description, price, and photos</strong>.
            This is exactly what they'll receive. Anything missing is flagged in amber below — click a “Fix”
            cell to jump straight to that listing and complete it before you submit.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center text-[#737373]">Building preview…</div>
        ) : (
          <>
            <div className="flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 text-[#047857] font-medium">
                <CheckCircle size={16} weight="fill" /> {rows.length - incompleteCount} ready
              </span>
              {incompleteCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[#B45309] font-medium" data-testid="incomplete-count">
                  <Warning size={16} weight="fill" /> {incompleteCount} with missing fields
                </span>
              )}
              <span className="text-[#A3A3A3]">{rows.length} item{rows.length !== 1 ? "s" : ""} · {scope === "approved" ? "approved only" : "all listings"}</span>
            </div>

            <div className="overflow-auto max-h-[55vh] border border-[#E5E5E5] rounded-lg mt-2" data-testid="export-preview-table">
              <table className="text-xs whitespace-nowrap">
                <thead className="sticky top-0 bg-[#FDFBF7] z-10">
                  <tr className="border-b border-[#E5E5E5]">
                    <th className="px-3 py-2 text-left label-caps sticky left-0 bg-[#FDFBF7]">#</th>
                    {columns.map((c) => (
                      <th key={c} className={`px-3 py-2 text-left font-semibold ${REQUIRED_1STDIBS.includes(c) ? "text-[#0D1B2A]" : "text-[#737373]"}`}>
                        {c}{REQUIRED_1STDIBS.includes(c) && <span className="text-[#B91C1C]">*</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-[#F0F0F0] align-top" data-testid={`preview-row-${i}`}>
                      <td className="px-3 py-2 sticky left-0 bg-white text-[#A3A3A3]">{i + 1}</td>
                      {columns.map((c) => {
                        const val = String(r[c] ?? "").trim();
                        const missing = REQUIRED_1STDIBS.includes(c) && !val;
                        if (missing) {
                          return (
                            <td key={c} className="px-3 py-2 max-w-[220px]">
                              <button
                                data-testid={`fix-${c.replace(/\s+/g, "-").toLowerCase()}-${i}`}
                                onClick={() => fixField(r._listing_id, c)}
                                className="inline-flex items-center gap-1 rounded bg-[#FEF3C7] text-[#B45309] font-medium px-2 py-0.5 hover:bg-[#FDE68A] transition-colors"
                              >
                                <Warning size={11} weight="fill" /> Fix
                              </button>
                            </td>
                          );
                        }
                        return (
                          <td key={c} className="px-3 py-2 max-w-[220px] truncate text-[#525252]" title={val}>
                            {(c === "Images" ? `${val.split(" | ").length} photo(s)` : val) || "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-[#A3A3A3]"><span className="text-[#B91C1C]">*</span> Required by 1stDibs · amber cells need attention before submitting</p>
              <div className="flex items-center gap-2">
                <button onClick={onClose} data-testid="preview-close-button" className="border border-[#E5E5E5] bg-white text-[#0D1B2A] px-4 py-2 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm">
                  Keep editing
                </button>
                <button onClick={onDownload} data-testid="preview-download-button" className="bg-[#0D1B2A] text-white px-5 py-2 rounded-md hover:bg-black transition-colors font-medium text-sm inline-flex items-center gap-2">
                  <DownloadSimple size={15} weight="bold" /> Download 1stDibs CSV
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
